/**
 * Pure helper functions for identifying and classifying rate schedules.
 * Shared across components, utils, and renderers.
 */

import { CoverageTierEnum, RateDesignEnum } from "@platform/openapi-types";

import type { RateSchedule } from "../types";

const isScheduleForCoverageTier = (
  schedule: RateSchedule,
  rateDesign: RateDesignEnum,
  coverageTierCodes: CoverageTierEnum[],
): boolean => {
  const rates = schedule.rates || [];
  const hasCompanionCoverageIndicator =
    schedule.rateCoverageIndicator == null ||
    schedule.rateCoverageIndicator === "life";

  return (
    schedule.rateDesign === rateDesign &&
    hasCompanionCoverageIndicator &&
    rates.length > 0 &&
    rates.every(
      (r) =>
        r.coverageTierCode && coverageTierCodes.includes(r.coverageTierCode),
    )
  );
};

/**
 * Checks whether a schedule is a companion childOnly flat rate schedule.
 * These schedules hold child rates separately from the primary age-banded schedule.
 */
export function isChildFlatRateSchedule(schedule: RateSchedule): boolean {
  return isScheduleForCoverageTier(schedule, RateDesignEnum.flatRate, [
    CoverageTierEnum.childOnly,
  ]);
}

const isChildCompositeSchedule = (schedule: RateSchedule): boolean =>
  isScheduleForCoverageTier(schedule, RateDesignEnum.composite, [
    CoverageTierEnum.childOnly,
  ]);

/**
 * Checks whether a schedule is a companion child-only schedule. SunLife sends the
 * Vol Life child rate as a composite schedule instead of a flat rate one, so a
 * child-only composite schedule counts too, but only when the plan has another
 * schedule to be the primary. A plan whose sole life schedule is child-only
 * composite renders that schedule as its primary.
 */
export function isChildCompanionSchedule(
  schedule: RateSchedule,
  schedules: readonly RateSchedule[],
): boolean {
  if (isChildFlatRateSchedule(schedule)) return true;
  if (!isChildCompositeSchedule(schedule)) return false;

  return schedules.some(
    (other) =>
      other !== schedule &&
      other.rateCoverageIndicator !== "adnd" &&
      !isCompanionFlatRateSchedule(other) &&
      !isChildCompositeSchedule(other),
  );
}

const ALLOWED_COVERAGE_TIER_CODES = [
  CoverageTierEnum.spouseChildren,
  CoverageTierEnum.childOnly,
];
/**
 * Checks whether a schedule is a companion flat rate schedule (e.g., spouse or child rates).
 * These schedules hold rates separately from the primary age-banded schedule.
 */
export function isCompanionFlatRateSchedule(schedule: RateSchedule): boolean {
  return isScheduleForCoverageTier(
    schedule,
    RateDesignEnum.flatRate,
    ALLOWED_COVERAGE_TIER_CODES,
  );
}

/**
 * Checks whether a schedule is a companion schedule: a flat rate spouse/child
 * schedule, or a child-only composite schedule next to a primary schedule.
 */
export function isCompanionSchedule(
  schedule: RateSchedule,
  schedules: readonly RateSchedule[],
): boolean {
  return (
    isCompanionFlatRateSchedule(schedule) ||
    isChildCompanionSchedule(schedule, schedules)
  );
}

/**
 * Reports whether a schedule's per-tier lives are really one plan-wide total
 * repeated on every tier. Some carriers (Unum) send the plan's enrolled-lives
 * count on each rate row instead of a per-tier breakdown, which reads as every
 * tier having the same enrollment. The tell is that each tier equals the
 * schedule-level total: four tiers of 24 cannot sum to a plan total of 24, whereas a real
 * breakdown sums to it. A total of zero is excluded, since all-zero tiers do sum to zero and
 * so are indistinguishable from a genuine empty breakdown. A legitimate zero tier is never
 * hidden: it can't equal the positive total this predicate requires, so any schedule
 * containing one fails the every() check and renders all of its per-tier values.
 */
export function isPlanTotalRepeatedPerTier(schedule: RateSchedule): boolean {
  const planTotal = schedule.numberOfLives;
  const rates = schedule.rates || [];

  return (
    planTotal != null &&
    planTotal > 0 &&
    rates.length > 1 &&
    rates.every((r) => r.numberOfLives === planTotal)
  );
}

/**
 * Finds a separate age-banded life schedule that holds only spouseOnly rates.
 * Some carriers (Unum) send EE and SP as two distinct ageBanded schedules
 * instead of mixing both coverageTierCodes within one schedule. When present,
 * the renderer must pull SP rates from this schedule rather than the primary one.
 *
 * The spouse schedule has to share the primary's rate design, because that
 * design is what decides the layout the two are rendered side by side in — a
 * tobacco-split spouse table has no column to sit in beside a plain age-banded
 * employee table. Matching the primary rather than hardcoding one design is
 * what lets a carrier splitting an ageBandedTobacco quote across two schedules
 * (Reliance Matrix) show its spouse rates instead of dropping them.
 */
export function resolveSpouseAgeBandedSchedule(
  schedules: RateSchedule[],
  primarySchedule: RateSchedule | undefined,
): RateSchedule | undefined {
  const design =
    primarySchedule?.rateDesign === "ageBandedTobacco"
      ? "ageBandedTobacco"
      : "ageBanded";

  return schedules.find(
    (s) =>
      s !== primarySchedule &&
      s.rateDesign === design &&
      (s.rateCoverageIndicator == null || s.rateCoverageIndicator === "life") &&
      (s.rates ?? []).length > 0 &&
      s.rates!.every((r) => r.coverageTierCode === CoverageTierEnum.spouseOnly),
  );
}

/**
 * Picks the schedule a plan's rate grid should display. Companion (spouse/child
 * flat) schedules render in their own section, and so do AD&D schedules — so
 * neither may win the primary slot just by coming first in the list (carriers
 * like Guardian send the AD&D schedule before the age-banded life schedule).
 * Falls back to the first non-companion schedule so AD&D-only plans still render.
 */
export function resolvePrimaryRateSchedule(
  schedules: RateSchedule[],
): RateSchedule | undefined {
  const nonCompanionSchedules = schedules.filter(
    (s) => !isCompanionSchedule(s, schedules),
  );

  return (
    nonCompanionSchedules.find((s) => s.rateCoverageIndicator !== "adnd") ??
    nonCompanionSchedules[0]
  );
}
